import type { PointerEvent as ReactPointerEvent } from 'react';

import { MapOverlay, MapPolygon } from '@/components/maps';
import { boundsOfRings } from '@/lib/maps/boundaries';
import { CIRCLE_HANDLE_BOX, DRAFT_BOUNDARY } from '@/lib/maps/boundary-style';
import type { FieldCircle } from '@/lib/maps/circle';
import { boundingSquareCorners, circleRing, HANDLE_BEARINGS_DEG } from '@/lib/maps/circle';

import styles from './map-circle-shape.module.scss';
import { MapMarkerTooltip } from './map-marker-tooltip';
import markerStyles from './marked-points.module.scss';

export type MapCircleShapeProps = {
  /** The circle to draw. During a handle drag this is the circle at its LIVE Radius, so the ring,
   *  the box and all four handles follow the pointer together. */
  circle: FieldCircle;
  /** Whether the **Mid Point**'s tooltip is open. */
  tooltipOpen: boolean;
  onSelectMidPoint: () => void;
  onDeleteMidPoint: () => void;
  /**
   * A handle's own press, forwarded raw. The gesture — threshold, live preview, commit or revert —
   * belongs to the page, which owns the projection, the container's rect and the placement check;
   * the same division `<MarkedPoints>`' `onPointerDown` draws.
   *
   * **No bearing is passed**, and none is needed: `radiusFromHandleDrag` reads the distance from the
   * **Mid Point**, and a bounding box's corner sits at `radius·√2` from the centre in all four
   * directions — so which handle was grabbed cannot change the answer.
   */
  onHandlePointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
};

/**
 * The circle being drawn (Figma `598:538815` canvas, `598:539872`–`598:539878` leaves): the
 * materialised ring, the dashed square that bounds it, its four corner handles, and the **Mid
 * Point** with its own tooltip.
 *
 * The ring is spherical trig, never a screen-space ellipse (`circleRing`) — and it is for
 * **rendering only**: placement sends the circle itself and the server samples the rim (grilling
 * decision 15), so these 64 points never go on the wire.
 *
 * Ring and box are both `<MapPolygon>`, unmodified: the box simply carries a zero-opacity fill and a
 * dash whose pitch equals its length, which reads as the 1px hairline Figma draws. Both are
 * `interactive={false}` — the shape is not a click target; the handles and the Mid Point are.
 *
 * The Mid Point reuses `marked-points.module.scss`'s `.point` and `.stack` verbatim — Figma's own
 * ellipse (`598:539878`, 21px, white, 2px black ring) is the **Point** marker with no number in it —
 * and never its `.recent` (black) state: a circle has exactly one Mid Point, so there is no "most
 * recently marked" for the highlight to move between.
 */
export function MapCircleShape({
  circle,
  tooltipOpen,
  onSelectMidPoint,
  onDeleteMidPoint,
  onHandlePointerDown,
}: MapCircleShapeProps) {
  const ring = circleRing(circle);
  const corners = boundingSquareCorners(circle);

  return (
    <>
      {/* `boundsOfRings` cannot return null here: both rings are non-empty by construction, which
          is its only null. */}
      <MapPolygon
        rings={[ring]}
        bounds={boundsOfRings([ring])!}
        style={DRAFT_BOUNDARY}
        interactive={false}
      />
      <MapPolygon
        rings={[corners]}
        bounds={boundsOfRings([corners])!}
        style={CIRCLE_HANDLE_BOX}
        interactive={false}
      />
      <MapOverlay point={circle.midPoint}>
        <span className={markerStyles.stack}>
          {tooltipOpen ? (
            <MapMarkerTooltip
              title="Mid Point"
              point={circle.midPoint}
              // Always. The 3-**Point** minimum is about a polygon's corner count and does not
              // apply to a shape that is one Mid Point (113601).
              canDelete
              deleteLabel="Delete Mid Point"
              onDelete={onDeleteMidPoint}
            />
          ) : null}
          <button
            type="button"
            className={markerStyles.point}
            aria-label="Mid Point"
            onClick={onSelectMidPoint}
          />
        </span>
      </MapOverlay>
      {/* AFTER the Mid Point, and that ordering is the fix: every `<MapOverlay>` appends into the
          same Google `floatPane`, so DOM order is paint order and the 21px Mid Point marker was
          covering all four handles. At a small projected radius the handles sit within the marker's
          own footprint — 11px out at `STANDARD_ZOOM` and the default Radius — so whichever is drawn
          last is the one the Grower can actually grab. It has to be the handles: the Mid Point's own
          press only opens a tooltip, while a handle press is the resize gesture. */}
      {corners.map((corner, index) => (
        // Keyed by BEARING, not index: a corner's compass direction is its identity, and it is
        // stable across every resize.
        <MapOverlay key={HANDLE_BEARINGS_DEG[index]} point={corner}>
          <span className={styles.center}>
            <button
              type="button"
              className={styles.handle}
              aria-label="Resize handle"
              onPointerDown={onHandlePointerDown}
            />
          </span>
        </MapOverlay>
      ))}
    </>
  );
}
